import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSeasonYear, useSeasonYearContext } from '../context/SeasonYearContext';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  MenuItem,
  Menu,
  Button,
  Alert,
  TextField,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Stack,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams, GridValueGetterParams, GridValueFormatterParams } from '@mui/x-data-grid';
import { API_URL } from '../config';
import { fetchApi, authedFetch } from '../utils/apiUtils';

interface Division {
  division_id: number;
  division_name: string;
}

interface Region {
  region_id: number;
  region_name: string;
}

interface RaceResultEntry {
  position: number;
  driver: string;
}

interface RaceResults {
  FEATURE?: RaceResultEntry[];
  LAST_CHANCE?: RaceResultEntry[];
  C_MAIN?: RaceResultEntry[];
  D_MAIN?: RaceResultEntry[];
  E_MAIN?: RaceResultEntry[];
}

interface ReportEvent {
  event_id: number;
  event_date: string;
  event_name: string;
  track_name: string;
  track_city?: string;
  track_state?: string;
  division_name: string;
  region_name: string;
  winners: string | null;
  total_entries: number;
  event_car_count: number;
  race_results?: RaceResults;
}

interface ReportSummary {
  total_events?: number;
  total_unique_competitors?: number;
  average_competitors_per_event?: number;
  events_without_results?: number;
}

interface ReportData {
  events: ReportEvent[];
  summary: ReportSummary;
}

interface Filters {
  division_id: string;
  start_date: string;
  end_date: string;
  region_id: string;
}

interface AlertState {
  show: boolean;
  message: string;
  severity: 'info' | 'success' | 'warning' | 'error';
}

const RACE_TYPE_LABELS: Record<keyof RaceResults, string> = {
  FEATURE: 'A-Main',
  LAST_CHANCE: 'B-Main',
  C_MAIN: 'C-Main',
  D_MAIN: 'D-Main',
  E_MAIN: 'E-Main',
};

const RACE_TYPE_ORDER: (keyof RaceResults)[] = ['FEATURE', 'LAST_CHANCE', 'C_MAIN', 'D_MAIN', 'E_MAIN'];

function HoosierAuditReports(): React.ReactElement {
  const seasonYear = useSeasonYear();
  const { setSeasonYear: setGlobalSeasonYear } = useSeasonYearContext();
  const [seasonAnchorEl, setSeasonAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<ReportData>({ events: [], summary: {} });
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([seasonYear]);
  const [alert, setAlert] = useState<AlertState>({ show: false, message: '', severity: 'info' });

  const [filters, setFilters] = useState<Filters>({
    division_id: '',
    start_date: '',
    end_date: '',
    region_id: ''
  });
  const reportRequestRef = useRef<AbortController | null>(null);

  const fetchDivisions = useCallback(async (): Promise<void> => {
    try {
      const response = await authedFetch(`${API_URL}/divisions`);
      if (!response.ok) throw new Error('Failed to fetch divisions');
      const divisionData = await response.json();
      setDivisions(Array.isArray(divisionData) ? divisionData : []);
    } catch (err) {
      console.error('Error fetching divisions:', err);
    }
  }, []);

  const fetchAvailableSeasons = useCallback(async (): Promise<void> => {
    try {
      const response = await authedFetch(`${API_URL}/config/available-seasons`);
      if (!response.ok) throw new Error('Failed to fetch available seasons');
      const { years } = await response.json();
      if (Array.isArray(years) && years.length > 0) setAvailableSeasons(years);
    } catch (err) {
      console.error('Error fetching available seasons:', err);
    }
  }, []);

  const fetchDivisionRegions = useCallback(async (divisionId: string): Promise<void> => {
    try {
      const response = await authedFetch(`${API_URL}/division-regions/${divisionId}?season_year=${seasonYear}`);
      if (!response.ok) throw new Error('Failed to fetch regions');
      const data = await response.json();
      const regionList = Array.isArray(data) ? data.map((dr: { region_id: number; region_name: string }) => ({
        region_id: dr.region_id,
        region_name: dr.region_name,
      })) : [];
      setRegions(regionList);
    } catch (err) {
      console.error('Error fetching division regions:', err);
      setRegions([]);
    }
  }, [seasonYear]);

  const fetchReportData = useCallback(async (): Promise<void> => {
    reportRequestRef.current?.abort();
    const controller = new AbortController();
    reportRequestRef.current = controller;

    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('season_year', String(seasonYear));

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '') {
          queryParams.append(key, String(value));
        }
      });

      queryParams.append('export_all', 'true');

      const reportData = await fetchApi(`${API_URL}/races/hoosier-audit?${queryParams.toString()}`, {
        signal: controller.signal,
      }) as ReportData;

      if (controller.signal.aborted) return;
      setData(reportData);
      setAlert({ show: false, message: '', severity: 'info' });

    } catch (err) {
      if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
      console.error('Error fetching report data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setAlert({
        show: true,
        message: `Error loading report: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [seasonYear, filters]);

  useEffect(() => {
    fetchDivisions();
    fetchAvailableSeasons();
  }, [fetchDivisions, fetchAvailableSeasons]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  useEffect(() => {
    if (filters.division_id) {
      fetchDivisionRegions(filters.division_id);
    } else {
      setRegions([]);
    }
  }, [filters.division_id, fetchDivisionRegions]);

  useEffect(() => {
    setFilters((prev) => (prev.region_id ? { ...prev, region_id: '' } : prev));
  }, [seasonYear]);

  const handleFilterChange = (field: keyof Filters, value: string): void => {
    if (field === 'division_id') {
      setFilters(prev => ({ ...prev, division_id: value, region_id: '' }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const formatRaceResults = (raceResults: RaceResults | undefined): string => {
    if (!raceResults || Object.keys(raceResults).length === 0) {
      return 'No results recorded';
    }

    const formattedSections: string[] = [];
    RACE_TYPE_ORDER.forEach((raceType) => {
      const results = raceResults[raceType];
      if (results && results.length > 0) {
        const drivers = results.map((r) => `${r.position}. ${r.driver}`).join(', ');
        formattedSections.push(`${RACE_TYPE_LABELS[raceType]}: ${drivers}`);
      }
    });

    return formattedSections.join(' | ');
  };

  const handleExport = async (exportAll: boolean = false): Promise<void> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('season_year', String(seasonYear));
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== '') {
          queryParams.append(key, String(value));
        }
      });

      if (exportAll) {
        queryParams.append('export_all', 'true');
      }

      const response = await authedFetch(`${API_URL}/races/hoosier-audit?${queryParams.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch data for export');

      const reportData: ReportData = await response.json();

      const csvHeaders = [
        'Event Date',
        'Event Name',
        'Track',
        'City/State',
        'Division',
        'Region',
        'Winner(s)',
        'Total Entries',
        'Event Car Count',
        'Race Results'
      ];

      const csvRows = reportData.events.map(event => [
        new Date(event.event_date).toLocaleDateString(),
        event.event_name || '',
        event.track_name || '',
        (() => {
          const city = event?.track_city || '';
          const state = event?.track_state || '';
          if (!city && !state) return 'Unknown';
          return `${city}${city && state ? ', ' : ''}${state}`;
        })(),
        event.division_name || '',
        event.region_name || '',
        event.winners || 'No Winner Recorded',
        event.total_entries || 0,
        event.event_car_count || 0,
        formatRaceResults(event.race_results)
      ]);

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.map(field => {
          if (typeof field === 'string' && field.includes(',')) {
            return `"${field}"`;
          }
          return field;
        }).join(','))
      ].join('\n');

      let filename = 'DIRTcar_Points_Report';
      
      if (filters.division_id) {
        const selectedDivision = divisions.find(d => d.division_id === parseInt(filters.division_id));
        if (selectedDivision) {
          filename += `_${selectedDivision.division_name.replace(/[^a-zA-Z0-9]/g, '_')}`;
        }
      }
      
      if (filters.region_id) {
        const selectedRegion = regions.find(r => r.region_id === parseInt(filters.region_id));
        if (selectedRegion) {
          filename += `_${selectedRegion.region_name.replace(/[^a-zA-Z0-9]/g, '_')}`;
        }
      } else if (!filters.region_id && filters.division_id) {
        filename += '_National';
      }
      
      filename += `_${seasonYear}_${new Date().toISOString().split('T')[0]}.csv`;

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const recordCount = reportData.events.length;
      setAlert({ 
        show: true, 
        message: `Report exported successfully! ${recordCount} records exported.`, 
        severity: 'success' 
      });

    } catch (err) {
      console.error('Export error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setAlert({ 
        show: true, 
        message: `Export failed: ${errorMessage}`, 
        severity: 'error' 
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'event_date',
      headerName: 'Date',
      width: 110,
      valueFormatter: (params: GridValueFormatterParams) => {
        if (!params.value) return '';
        return new Date(params.value as string).toLocaleDateString();
      }
    },
    { field: 'event_name', headerName: 'Event Name', flex: 2, minWidth: 200 },
    { field: 'track_name', headerName: 'Track', flex: 1, minWidth: 150 },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 150,
      valueGetter: (params: GridValueGetterParams) => {
        const city = params.row?.track_city || '';
        const state = params.row?.track_state || '';
        if (!city && !state) return 'Unknown';
        return `${city}${city && state ? ', ' : ''}${state}`;
      }
    },
    { field: 'division_name', headerName: 'Division', width: 120 },
    { field: 'region_name', headerName: 'Region', width: 100 },
    {
      field: 'winners',
      headerName: 'Winner(s)',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          {params.value ? (
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {params.value}
            </Typography>
          ) : (
            <Chip
              label="No Winner Recorded"
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )
    },
    {
      field: 'event_car_count',
      headerName: 'Car Count',
      width: 100,
      type: 'number'
    },
    {
      field: 'race_results',
      headerName: 'Race Results',
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const raceResults = params.value as RaceResults | undefined;
        const present = RACE_TYPE_ORDER.filter((type) => (raceResults?.[type]?.length ?? 0) > 0);

        if (present.length === 0) {
          return (
            <Typography variant="caption" color="text.disabled">
              No results
            </Typography>
          );
        }

        return (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            {present.map((type) => {
              const drivers = (raceResults?.[type] ?? [])
                .map((r) => `${r.position}. ${r.driver}`)
                .join('\n');
              const ariaLabel = `${RACE_TYPE_LABELS[type]}: ${drivers.replace(/\n/g, ', ')}`;
              return (
                <Tooltip
                  key={type}
                  title={<Box sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem' }}>{drivers}</Box>}
                  arrow
                  placement="top"
                >
                  <Chip
                    label={RACE_TYPE_LABELS[type]}
                    size="small"
                    variant="outlined"
                    aria-label={ariaLabel}
                    onClick={() => {}}
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, cursor: 'default' }}
                  />
                </Tooltip>
              );
            })}
          </Stack>
        );
      },
    },
  ];

  const eventCount = data.events?.length || data.summary.total_events || 0;
  const missingResults = data.summary.events_without_results ?? 0;
  const stats = [
    {
      label: 'Events',
      value: eventCount,
      icon: <EventIcon />,
    },
    {
      label: 'Competitors',
      value: data.summary.total_unique_competitors ?? 0,
      icon: <GroupIcon />,
    },
    {
      label: 'Avg per Event',
      value: data.summary.average_competitors_per_event ?? 0,
      icon: <TrendingUpIcon />,
    },
    {
      label: 'Missing Results',
      value: missingResults,
      icon: <WarningAmberIcon />,
      tone: missingResults > 0 ? 'warning' : 'default',
    },
  ] as const;

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Reports
          </Typography>
          <Button
            onClick={(e) => setSeasonAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 9999,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.8125rem',
              py: 0.25,
              px: 1.5,
              minHeight: 28,
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.disabled' },
            }}
          >
            <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.75, fontWeight: 500 }}>
              Season
            </Typography>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {seasonYear}
            </Typography>
          </Button>
          <Menu
            anchorEl={seasonAnchorEl}
            open={Boolean(seasonAnchorEl)}
            onClose={() => setSeasonAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 100 } } }}
          >
            {availableSeasons.map((y) => (
              <MenuItem
                key={y}
                selected={y === seasonYear}
                onClick={() => {
                  setGlobalSeasonYear(y);
                  setSeasonAnchorEl(null);
                }}
              >
                {y}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={fetchReportData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport(true)}
            disabled={loading || data.events.length === 0}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid key={stat.label} item xs={6} sm={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {stat.label}
                  </Typography>
                  <Box
                    sx={{
                      color: stat.tone === 'warning' ? 'warning.main' : 'text.disabled',
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1.1,
                    color: stat.tone === 'warning' ? 'warning.main' : 'text.primary',
                  }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            select
            label="Division"
            size="small"
            sx={{ minWidth: 180 }}
            value={filters.division_id}
            onChange={(e) => handleFilterChange('division_id', e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">All Divisions</MenuItem>
            {divisions.map((division) => (
              <MenuItem key={division.division_id} value={String(division.division_id)}>
                {division.division_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Region"
            size="small"
            sx={{ minWidth: 160 }}
            value={filters.region_id}
            onChange={(e) => handleFilterChange('region_id', e.target.value)}
            disabled={!filters.division_id}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">All Regions</MenuItem>
            {regions.map((region) => (
              <MenuItem key={region.region_id} value={String(region.region_id)}>
                {region.region_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Start Date"
            type="date"
            size="small"
            sx={{ width: 160 }}
            InputLabelProps={{ shrink: true }}
            value={filters.start_date}
            onChange={(e) => handleFilterChange('start_date', e.target.value)}
          />

          <TextField
            label="End Date"
            type="date"
            size="small"
            sx={{ width: 160 }}
            InputLabelProps={{ shrink: true }}
            value={filters.end_date}
            onChange={(e) => handleFilterChange('end_date', e.target.value)}
          />
        </Box>
      </Paper>

      {alert.show && (
        <Alert
          severity={alert.severity}
          sx={{ mb: 2 }}
          onClose={() => setAlert({ ...alert, show: false })}
        >
          {alert.message}
        </Alert>
      )}

      <Paper sx={{ height: 700, width: '100%' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={data.events}
            columns={columns}
            getRowId={(row) => row.event_id}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
                csvOptions: { disableToolbarButton: true },
                printOptions: { disableToolbarButton: true },
              },
            }}
            disableSelectionOnClick
            pagination
            pageSize={100}
            rowsPerPageOptions={[25, 50, 100, 200]}
            sx={{
              border: 'none',
              fontFamily: 'inherit',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'transparent',
                borderBottom: '1px solid',
                borderColor: 'divider',
                minHeight: '40px !important',
                maxHeight: '40px !important',
                lineHeight: '40px !important',
              },
              '& .MuiDataGrid-columnHeader': {
                padding: '0 12px',
                '&:focus, &:focus-within': { outline: 'none' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: '0.75rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'text.secondary',
              },
              '& .MuiDataGrid-columnSeparator': { display: 'none' },
              '& .MuiDataGrid-cell': {
                padding: '0 12px',
                fontSize: '0.875rem',
                color: 'text.primary',
                borderBottom: '1px solid',
                borderColor: 'grey.100',
                '&:focus, &:focus-within': { outline: 'none' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              },
              '& .MuiDataGrid-row': {
                transition: 'background-color 120ms',
                '&:hover': { backgroundColor: 'action.hover' },
              },
              '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
                borderBottom: 'none',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid',
                borderColor: 'divider',
                minHeight: '44px',
              },
              '& .MuiTablePagination-root': {
                fontSize: '0.8125rem',
              },
            }}
          />
        )}
      </Paper>
    </Box>
  );
}

export default HoosierAuditReports;
